import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import DeleteIcon from 'assets/settings/components/DeleteIcon';
import EditIcon from 'assets/settings/components/EditIcon';
import Select from 'common-ui/select/Select';
import Pagination from 'common-ui/table/Pagination';
import { getAdministrativeRegions } from 'features/newsales/newsalesActions';
import {
  selectAdministrativeRegions,
  selectAdministrativeRegionsCurrentPage,
  selectAdministrativeRegionsDeleteSuccess,
  selectAdministrativeRegionsLoading,
  selectAdministrativeRegionsPagesCount,
  selectAdministrativeRegionsSearchData,
  selectAdministrativeRegionsSelectedCountryId,
  selectAdministrativeRegionsSuccess,
  selectAdministrativeRegionsUpdateSuccess,
  setAdministrativeRegionsCreateSuccess,
  setAdministrativeRegionsCurrentPage,
  setAdministrativeRegionsDeleteSuccess,
  setAdministrativeRegionsSearchData,
  setAdministrativeRegionsSelectedCountry,
  setAdministrativeRegionsUpdateSuccess,
} from 'features/newsales/newsalesSlice';

import {
  ActionsCell,
  EmptyState,
  FilterSection,
  IconButton,
  LoadingSpinner,
  PaginationWrapper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableHeaderActions,
  TableRow,
} from '../Addresses.styles';

const RegionPage = ({ onEdit, onDelete, allCountries }) => {
  const dispatch = useDispatch();

  const regions = useSelector(selectAdministrativeRegions);
  const regionsLoading = useSelector(selectAdministrativeRegionsLoading);
  const regionsCurrentPage = useSelector(selectAdministrativeRegionsCurrentPage);
  const regionsPagesCount = useSelector(selectAdministrativeRegionsPagesCount);
  const regionsSearchData = useSelector(selectAdministrativeRegionsSearchData);
  const regionsCreateSuccess = useSelector(selectAdministrativeRegionsSuccess);
  const regionsUpdateSuccess = useSelector(selectAdministrativeRegionsUpdateSuccess);
  const regionsDeleteSuccess = useSelector(selectAdministrativeRegionsDeleteSuccess);
  const selectedCountryId = useSelector(selectAdministrativeRegionsSelectedCountryId);

  // Fetch regions when country is selected or searchData changes
  useEffect(() => {
    if (selectedCountryId) {
      dispatch(
        getAdministrativeRegions({
          countryId: selectedCountryId,
          page: regionsCurrentPage,
          limit: regionsSearchData.limit,
          search: regionsSearchData.search,
        })
      );
    }
  }, [
    dispatch,
    selectedCountryId,
    regionsCurrentPage,
    regionsSearchData.limit,
    regionsSearchData.search,
  ]);

  useEffect(() => {
    if (regionsCreateSuccess) {
      dispatch(setAdministrativeRegionsCreateSuccess(false));
      if (selectedCountryId) {
        dispatch(
          getAdministrativeRegions({
            countryId: selectedCountryId,
            page: regionsCurrentPage,
            limit: regionsSearchData.limit,
            search: regionsSearchData.search,
          })
        );
      }
    }
  }, [
    regionsCreateSuccess,
    dispatch,
    selectedCountryId,
    regionsCurrentPage,
    regionsSearchData.limit,
    regionsSearchData.search,
  ]);

  useEffect(() => {
    if (regionsUpdateSuccess) {
      dispatch(setAdministrativeRegionsUpdateSuccess(false));
    }
  }, [regionsUpdateSuccess, dispatch]);

  useEffect(() => {
    if (regionsDeleteSuccess) {
      dispatch(setAdministrativeRegionsDeleteSuccess(false));
      if (regions?.length === 1 && regionsCurrentPage > 1) {
        dispatch(setAdministrativeRegionsCurrentPage(regionsCurrentPage - 1));
      } else {
        // Refetch current page data after deletion
        if (selectedCountryId) {
          dispatch(
            getAdministrativeRegions({
              countryId: selectedCountryId,
              page: regionsCurrentPage,
              limit: regionsSearchData.limit,
              search: regionsSearchData.search,
            })
          );
        }
      }
    }
  }, [
    regionsDeleteSuccess,
    dispatch,
    regions,
    regionsCurrentPage,
    selectedCountryId,
    regionsSearchData.limit,
    regionsSearchData.search,
  ]);

  const handleCountrySelect = (selectedOption) => {
    const countryId = selectedOption ? selectedOption.value : null;
    dispatch(setAdministrativeRegionsSelectedCountry(countryId));
  };

  const handlePageChange = (page) => {
    dispatch(setAdministrativeRegionsCurrentPage(page));
  };

  const handleRowCountChange = (count) => {
    dispatch(setAdministrativeRegionsSearchData({ limit: count, offset: 0 }));
    dispatch(setAdministrativeRegionsCurrentPage(1));
  };

  const countryOptions = allCountries.map((country) => ({
    value: country.id,
    label: country.name,
  }));

  const selectedCountryOption = selectedCountryId
    ? countryOptions.find((option) => option.value === selectedCountryId)
    : null;

  return (
    <>
      <FilterSection>
        <Select
          label="Select Country"
          value={selectedCountryOption}
          onChange={handleCountrySelect}
          options={countryOptions}
          placeholder="Select a country"
          isClearable
          isDisabled={allCountries.length === 0}
        />
      </FilterSection>

      {regionsLoading ? (
        <LoadingSpinner>Loading...</LoadingSpinner>
      ) : !selectedCountryId ? (
        <EmptyState>Please select a country to view regions.</EmptyState>
      ) : !regions || regions.length === 0 ? (
        <EmptyState>No regions found.</EmptyState>
      ) : (
        <>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeaderActions>Actions</TableHeaderActions>
              </TableRow>
            </TableHead>
            <TableBody>
              {regions.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <ActionsCell>
                    <IconButton onClick={() => onEdit(item)}>
                      <EditIcon size={18} color="#2D6CDF" />
                    </IconButton>
                    <IconButton onClick={() => onDelete(item)}>
                      <DeleteIcon size={18} color="#FB2C36" />
                    </IconButton>
                  </ActionsCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {regionsPagesCount > 1 && (
            <PaginationWrapper>
              <Pagination
                currentPage={regionsCurrentPage}
                totalPages={regionsPagesCount}
                onPageChange={handlePageChange}
                handleRowCountChange={handleRowCountChange}
                dataCount={regions.length}
                count={regionsSearchData.limit}
                editableRowCount
              />
            </PaginationWrapper>
          )}
        </>
      )}
    </>
  );
};

export default RegionPage;
